import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { generatePath, Link as RouterLink } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { get } from 'lodash';
import { List, ListItem, ListSubheader, Box, Avatar, CardContent, styled } from '@mui/material';
import { Today as TodayIcon, History as HistoryIcon, Update as UpdateIcon } from '@mui/icons-material';
import useActiveUser from '../../../../../hooks/useActiveUser';
import RecentRescue from '../../../../../components/RescueNotification/RecentRescue';
import UpcomingRescue from '../../../../../components/RescueNotification/UpcomingRescue';
import routes from '../../../../../routes';
import RescueMobileCard from '../../../../Rescue/RescueMobileCard';
import useRescuerDashboardActions from '../../../../../hooks/useRescuerDashboardActions';
import { NoRescueCard, RESCUER_MOBILE_DASHBOARD_TAB_MY_RESCUES } from '../RescuerRescuesDashboardMobileContainer';
import { getRecentRescues, getTodayRescues, getUpcomingRescues } from '../../../../../helpers/RescuesHelper';
import { BaseCard } from '../../../../../components/Common/BaseCard';
import { Colors } from '../../../../../assets/theme/Colors';

const listSectionSx = {
  backgroundColor: 'inherit',
};

const listSubheaderSx = {
  typography: 'h5',
  top: -1,
  padding: 1,
  paddingBottom: 0,
  borderRadius: 4,
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0,
};

const listSubItemSx = {
  display: 'block',
  padding: 1,
};

const ulSx = {
  marginBottom: 3,
  backgroundColor: 'inherit',
  padding: 0,
};

const iconSx = {
  borderRadius: '12px',
  width: 50,
  height: 50,
};

const todaySx = {
  backgroundColor: Colors.rescues.today.color,
  filter: 'brightness(0.9)',
};

const recentSx = {
  backgroundColor: Colors.rescues.recent.color,
  filter: 'brightness(0.9)',
};

const upcomingSx = {
  backgroundColor: Colors.rescues.claimed.color,
  filter: 'brightness(0.9)',
};

const StyledBaseCard = styled(BaseCard, {
  shouldForwardProp: (prop) => prop !== 'color',
})(({ color }) => ({
  height: '100%',
  borderRadius: 16,
  backgroundColor: color,
}));

export const StyledSubHeader = ({ text, subText, icon, component = 'div', to = undefined, color }) => (
  <StyledBaseCard color={color} className="subheader">
    <CardContent style={{
      paddingTop: 8,
      paddingBottom: 8,
    }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          textDecoration: 'none',
        }}
        component={component}
        to={to}
      >
        <Box display="flex" flexDirection="column" alignItems="start" rowGap="4ox" pr={1}>
          {text && (
            <Box fontFamily="fontFamily" fontSize="h5.fontSize" color="#fff">
              {text}
            </Box>
          )}
          {subText && (
            <Box className="subtext" fontFamily="fontFamily" fontSize="caption.fontSize" color="#fff">
              {subText}
            </Box>
          )}
        </Box>
        {icon}
      </Box>
    </CardContent>
  </StyledBaseCard>
);

const MyFrusMobile = ({ history }) => {
  const scrollContainer = useRef();
  const dispatch = useDispatch();
  const activeUser = useActiveUser();
  const { fetchMyFrusTab } = useRescuerDashboardActions();
  const { formatMessage } = useIntl();

  const rescuesEntities = useSelector((state) => state.entities.rescues);
  const isLoading = get(rescuesEntities, `claimedAndAdoptedIds[${activeUser.id}].inflight`, true);
  const isLoadingRecentRescues = get(rescuesEntities, `pastIds[${activeUser.id}].inflight`, true);
  const todayRescues = getTodayRescues(activeUser.id, rescuesEntities);
  const recentRescues = getRecentRescues(activeUser.id, rescuesEntities);
  const upcomingRescues = getUpcomingRescues(activeUser.id, rescuesEntities);

  const onNotificationClick = (rescue) => history.push(generatePath(routes.rescue, { rescueId: rescue.id }));

  useEffect(() => {
    fetchMyFrusTab(false, false);
  }, [activeUser.id, dispatch]);

  return (
    <List
      sx={{    position: 'relative',
        height: '100%',
        width: '100%',
        overflow: 'auto',
        backgroundColor: 'background.default',
      }}
      subheader={<li />}
      ref={scrollContainer}
    >
      <li style={listSectionSx}>
        <Box component="ul" sx={ulSx}>
          <ListSubheader sx={listSubheaderSx}>
            <StyledSubHeader
              color={Colors.rescues.today.color}
              text={formatMessage({ id: 'rescuer-dashboard-mobile.my-frus.today-rescues' })}
              icon={
                <Avatar sx={[iconSx, todaySx]}>
                  <TodayIcon />
                </Avatar>
              }
            />
          </ListSubheader>
          {isLoading && (
            <ListItem key={`rescue-today-loader}`} sx={listSubItemSx}>
              <RescueMobileCard highlighted isLoading />
            </ListItem>
          )}
          {!isLoading && todayRescues.length === 0 && (
            <ListItem sx={listSubItemSx}>
              <NoRescueCard text="You have no rescues today." />
            </ListItem>
          )}

          {!isLoading &&
            todayRescues.map((rescue) => (
              <ListItem key={`rescue-today-${rescue.id}`} sx={listSubItemSx}>
                <RecentRescue rescue={rescue} onClick={() => onNotificationClick(rescue)} />
              </ListItem>
            ))}
        </Box>
      </li>

      <li style={listSectionSx}>
        <Box component="ul" sx={ulSx}>
          <ListSubheader id="recent-rescues" sx={listSubheaderSx}>
            <StyledSubHeader
              color={Colors.rescues.recent.color}
              text={formatMessage({ id: 'rescuer-dashboard-mobile.my-frus.recent-rescues' })}
              subText={formatMessage({ id: 'rescuer-dashboard-mobile.my-frus.recent-rescues-sub' })}
              icon={
                <Avatar sx={[iconSx, recentSx]}>
                  <HistoryIcon />
                </Avatar>
              }
            />
          </ListSubheader>
          {isLoadingRecentRescues && (
            <ListItem key={`rescue-today-loader}`} sx={listSubItemSx}>
              <RescueMobileCard highlighted isLoading />
            </ListItem>
          )}
          {!isLoadingRecentRescues && recentRescues.length === 0 && (
            <ListItem sx={listSubItemSx}>
              <NoRescueCard text="You have no recent rescues." />
            </ListItem>
          )}

          {!isLoadingRecentRescues &&
            recentRescues.map((rescue) => (
              <ListItem key={`rescue-recent-${rescue.id}`} sx={listSubItemSx}>
                <RecentRescue rescue={rescue} onClick={() => onNotificationClick(rescue)} />
              </ListItem>
            ))}
        </Box>
      </li>

      <li style={listSectionSx}>
        <Box component="ul" sx={ulSx}>
          <ListSubheader sx={listSubheaderSx}>
            <StyledSubHeader
              color={Colors.rescues.claimed.color}
              component={RouterLink}
              to={generatePath(routes.mobileDashboardRescues, { tab: RESCUER_MOBILE_DASHBOARD_TAB_MY_RESCUES })}
              text={formatMessage({ id: 'rescuer-dashboard-mobile.my-frus.upcoming-rescues' })}
              subText={formatMessage({ id: 'rescuer-dashboard-mobile.my-frus.upcoming-rescues-sub' })}
              icon={
                <Avatar sx={[iconSx, upcomingSx]}>
                  <UpdateIcon />
                </Avatar>
              }
            />
          </ListSubheader>
          {isLoading && (
            <ListItem key={`rescue-upcoming-loader}`} sx={listSubItemSx}>
              <RescueMobileCard highlighted isLoading />
            </ListItem>
          )}
          {upcomingRescues.length === 0 && !isLoading && (
            <ListItem key={`rescue-upcoming-nodata`} sx={listSubItemSx}>
              <NoRescueCard text="You have no upcoming rescues." />
            </ListItem>
          )}

          {!isLoading &&
            upcomingRescues.map((rescue) => (
              <ListItem key={`rescue-upcoming-${rescue.id}`} sx={listSubItemSx}>
                <UpcomingRescue
                  key={rescue.id}
                  user={activeUser}
                  rescue={rescue}
                  onClick={() => onNotificationClick(rescue)}
                />
              </ListItem>
            ))}
        </Box>
      </li>
    </List>
  );
};

export default MyFrusMobile;
